function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

const THEME_RESOURCE_TIMEOUT = 10000;

function getThemeAssetUrl(url) {
  const assetUrl = new URL(url, document.baseURI);
  if (assetUrl.pathname.startsWith("/cdn/shop/")) {
    return `${assetUrl.pathname}${assetUrl.search}`;
  }
  return assetUrl.href;
}

function waitForThemeResource(element, timeout = THEME_RESOURCE_TIMEOUT) {
  return new Promise((resolve, reject) => {
    let timer;
    const cleanup = () => {
      clearTimeout(timer);
      element.removeEventListener("load", onLoad);
      element.removeEventListener("error", onError);
    };
    const onLoad = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(`Failed to load ${element.src || element.href}`));
    };

    element.addEventListener("load", onLoad, { once: true });
    element.addEventListener("error", onError, { once: true });
    timer = setTimeout(() => {
      cleanup();
      reject(new Error(`Timed out loading ${element.src || element.href}`));
    }, timeout);
  });
}

function loadProductScript() {
  if (customElements.get("product-form")) return Promise.resolve();
  if (window.productScriptPromise) return window.productScriptPromise;

  const src = new URL(getThemeAssetUrl(theme.routes.product_script_url), document.baseURI).href;
  const existing = Array.from(document.scripts).find((script) => script.src === src);
  const script = existing || document.createElement("script");

  window.productScriptPromise = waitForThemeResource(script).catch((error) => {
    if (!existing) script.remove();
    window.productScriptPromise = null;
    throw error;
  });

  if (!existing) {
    script.src = src;
    script.async = true;
    document.head.appendChild(script);
  }

  return window.productScriptPromise;
}

function loadPickupAvailabilityScript() {
  if (customElements.get("pickup-availability")) return Promise.resolve();
  if (window.pickupAvailabilityScriptPromise) {
    return window.pickupAvailabilityScriptPromise;
  }

  const src = new URL(
    getThemeAssetUrl(theme.routes.pickup_availability_script_url),
    document.baseURI
  ).href;
  const existing = Array.from(document.scripts).find((script) => script.src === src);
  const script = existing || document.createElement("script");

  window.pickupAvailabilityScriptPromise = waitForThemeResource(script).catch(
    (error) => {
      if (!existing) script.remove();
      window.pickupAvailabilityScriptPromise = null;
      throw error;
    }
  );

  if (!existing) {
    script.src = src;
    script.async = true;
    document.head.appendChild(script);
  }

  return window.pickupAvailabilityScriptPromise;
}

function loadStylesheet(url) {
  const href = new URL(getThemeAssetUrl(url), document.baseURI).href;
  const existing = Array.from(document.querySelectorAll('link[rel~="stylesheet"]')).find(
    (link) => link.href === href && link.dataset.themeResourceFailed !== "true"
  );

  if (existing && existing.sheet) return Promise.resolve();

  const link = existing || document.createElement("link");
  const loaded = waitForThemeResource(link).catch((error) => {
    if (existing) {
      link.dataset.themeResourceFailed = "true";
    } else {
      link.remove();
    }
    throw error;
  });

  if (!existing) {
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }

  return loaded;
}

function loadProductResources() {
  if (!window.productStylesPromise) {
    window.productStylesPromise = Promise.all([
      loadStylesheet(theme.routes.product_style_url),
      loadStylesheet(theme.routes.subscription_details_style_url),
    ]).catch((error) => {
      window.productStylesPromise = null;
      throw error;
    });
  }

  return Promise.all([
    loadProductScript(),
    loadPickupAvailabilityScript(),
    window.productStylesPromise,
  ]);
}

const LITE_YOUTUBE_SELECTOR = ".lite-youtube, .lite-youtube-dev, lite-youtube";
const LITE_YOUTUBE_RETRY_LIMIT = 2;

function containsLiteYoutube(root) {
  return Boolean(
    root?.matches?.(LITE_YOUTUBE_SELECTOR) ||
      root?.querySelector?.(LITE_YOUTUBE_SELECTOR),
  );
}

function loadLiteYoutubeScript(attempt = 0) {
  if (window.LiteYTEmbedDiv?.init) return Promise.resolve();
  if (window.liteYoutubeScriptPromise) return window.liteYoutubeScriptPromise;

  const script = document.createElement("script");
  const scriptUrl = new URL(
    getThemeAssetUrl(theme.routes.lite_youtube_script_url),
    document.baseURI,
  );
  if (attempt) scriptUrl.searchParams.set("lite_youtube_retry", String(attempt));
  script.type = "module";
  script.src = getThemeAssetUrl(scriptUrl.href);
  script.dataset.liteYoutubeLoader = "true";

  const request = waitForThemeResource(script).then(() => {
    if (!window.LiteYTEmbedDiv?.init) {
      throw new Error(`Loaded ${script.src} without initializing LiteYTEmbedDiv`);
    }
  });
  const promise = request.catch((error) => {
    script.remove();
    if (window.liteYoutubeScript === script) window.liteYoutubeScript = null;
    if (attempt >= LITE_YOUTUBE_RETRY_LIMIT) {
      if (window.liteYoutubeScriptPromise === promise) window.liteYoutubeScriptPromise = null;
      throw error;
    }

    return new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1))).then(() => {
      if (window.liteYoutubeScriptPromise === promise) window.liteYoutubeScriptPromise = null;
      return loadLiteYoutubeScript(attempt + 1);
    });
  });

  window.liteYoutubeScript = script;
  window.liteYoutubeScriptPromise = promise;
  document.head.appendChild(script);
  return promise;
}

function loadLiteYoutube(root = document) {
  if (!containsLiteYoutube(root)) return Promise.resolve();
  if (window.LiteYTEmbedDiv?.init) {
    window.LiteYTEmbedDiv.init(root);
    return Promise.resolve();
  }
  if (!theme.routes.lite_youtube_script_url) return Promise.resolve();

  return loadLiteYoutubeScript().then(() => window.LiteYTEmbedDiv.init(root));
}

function observeLiteYoutube() {
  if (window.liteYoutubeObserver || !document.documentElement) return;

  window.liteYoutubeObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE || !containsLiteYoutube(node)) return;
        loadLiteYoutube(node).catch((error) => console.error(error));
      });
    });
  });
  window.liteYoutubeObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

// The recipe modal is requested in the context of the current page only when a
// visitor reaches for a download button.
const RECIPE_DOWNLOAD_TRIGGER = "[data-recipe-download]";

function loadRecipeDownloadModal() {
  const rendered = document.getElementById("PopupModal-recipe");
  if (rendered) return Promise.resolve(rendered);
  if (window.recipeDownloadModalPromise) return window.recipeDownloadModalPromise;

  const sectionUrl = new URL(window.location.href);
  sectionUrl.searchParams.set("section_id", "recipe-download-modal");
  const url = `${sectionUrl.pathname}${sectionUrl.search}`;
  window.recipeDownloadModalPromise = fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`Failed to load ${url}: ${response.status}`);
      return response.text();
    })
    .then(async (html) => {
      const section = new DOMParser()
        .parseFromString(html, "text/html")
        .getElementById("shopify-section-recipe-download-modal");
      const modal = section && section.querySelector("#PopupModal-recipe");
      if (!modal) throw new Error(`${url} did not return the recipe download modal`);

      await Promise.allSettled(
        Array.from(section.querySelectorAll('link[rel="stylesheet"]'), (link) => {
          link.remove();
          return loadStylesheet(link.getAttribute("href"));
        })
      );

      const parsedScripts = Array.from(section.querySelectorAll("script"));
      document.body.appendChild(section);
      parsedScripts.forEach((parsed) => {
        const script = document.createElement("script");
        Array.from(parsed.attributes).forEach(({ name, value }) => script.setAttribute(name, value));
        script.textContent = parsed.textContent;
        parsed.replaceWith(script);
      });
      return modal;
    })
    .catch((error) => {
      window.recipeDownloadModalPromise = null;
      throw error;
    });

  return window.recipeDownloadModalPromise;
}

document.addEventListener("click", (event) => {
  const button = event.target.closest?.(RECIPE_DOWNLOAD_TRIGGER);
  if (!button) return;
  event.preventDefault();

  loadRecipeDownloadModal()
    .then((modal) => {
      modal.dispatchEvent(new CustomEvent("recipe-download:open", { detail: { button } }));
      modal.show(button);
    })
    .catch((error) => console.error(error));
});

["pointerover", "focusin"].forEach((type) => {
  document.addEventListener(type, (event) => {
    if (!event.target.closest?.(RECIPE_DOWNLOAD_TRIGGER)) return;
    loadRecipeDownloadModal().catch(() => {});
  });
});

/**
 *  @class
 *  @function Quantity
 */
class QuantityInput {
  constructor(self, decreaseText, increaseText) {
    this.input = self.querySelector(".qty");
    this.changeEvent = new Event("change", {
      bubbles: true,
    });
    // Create buttons
    this.subtract = self.querySelector(".minus");
    this.add = self.querySelector(".plus");

    // Add functionality to buttons
    this.subtract.addEventListener("click", () => this.change_quantity(-1));
    this.add.addEventListener("click", () => this.change_quantity(1));

    self.classList.add("buttons_added");
  }

  change_quantity(change) {
    // Get current value
    let quantity = Number(this.input.value);

    // Ensure quantity is a valid number
    if (isNaN(quantity)) quantity = 1;

    // Change quantity
    quantity += change;

    // Ensure quantity is always a number
    quantity = Math.max(quantity, 1);

    // Output number
    this.input.value = quantity;

    this.input.dispatchEvent(this.changeEvent);
  }
}
let quantities = document.querySelectorAll(".quantity:not(.buttons_added)");
quantities.forEach((quantity) => {
  new QuantityInput(quantity);
});

/**
 *  @class
 *  @function ArrowSubMenu
 */
class ArrowSubMenu {
  constructor(self) {
    this.submenu = self.parentNode.querySelector(".sub-menu");
    this.arrow = self;
    // Add functionality to buttons
    self.addEventListener("click", (e) => this.toggle_submenu(e));
  }

  toggle_submenu(e) {
    e.preventDefault();
    let submenu = this.submenu;

    if (!submenu.classList.contains("active")) {
      submenu.classList.add("active");
    } else {
      submenu.classList.remove("active");
      this.arrow.blur();
    }
  }
}
let arrows = document.querySelectorAll(".thb-arrow");
arrows.forEach((arrow) => {
  new ArrowSubMenu(arrow);
});

/**
 *  @class
 *  @function ProductCard
 */
if (!customElements.get("product-card")) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this.swatches = this.querySelector(".product-card-swatches");
      this.image = this.querySelector(
        ".product-featured-image-link .product-primary-image"
      );
      this.additional_images = this.querySelectorAll(
        ".product-secondary-image"
      );
      this.additional_images_nav = this.querySelectorAll(
        ".product-secondary-images-nav li"
      );
      this.quickview_button = this.querySelector(".product-card-quickview");
    }
    connectedCallback() {
      if (this.swatches) {
        this.enableSwatches(this.swatches, this.image);
      }
      if (this.quickview_button) {
        this.enableQuickview(this.quickview_button);
      }
      if (this.additional_images) {
        this.enableAdditionalImages();
      }
    }
    enableAdditionalImages() {
      let image_length = this.additional_images.length;
      let images = this.additional_images;
      let nav = this.additional_images_nav;
      let image_container = this.querySelector(".product-featured-image");
      const mousemove = function (e) {
        let l = e.offsetX;
        let w = this.getBoundingClientRect().width;
        let prc = l / w;
        let sel = Math.floor(prc * image_length);
        let selimg = images[sel];
        images.forEach((image, index) => {
          if (image.classList.contains("hover")) {
            image.classList.remove("hover");
            if (nav.length) {
              nav[index].classList.remove("active");
            }
          }
        });
        if (selimg) {
          if (!selimg.classList.contains("hover")) {
            selimg.classList.add("hover");
            if (nav.length) {
              nav[sel].classList.add("active");
            }
          }
        }
      };
      const mouseleave = function (e) {
        images.forEach((image, index) => {
          image.classList.remove("hover");
          if (nav.length) {
            nav[index].classList.remove("active");
          }
        });
      };
      if (!image_container) return false;

      image_container.addEventListener("touchstart", mousemove);
      image_container.addEventListener("touchmove", mousemove);
      image_container.addEventListener("touchend", mouseleave);
      image_container.addEventListener("mouseenter", mousemove);
      image_container.addEventListener("mousemove", mousemove);
      image_container.addEventListener("mouseleave", mouseleave);
    }
    enableSwatches(swatches, image) {
      let swatch_list = swatches.querySelectorAll(".product-card-swatch"),
        active = swatches.querySelector(".product-card-swatch.active");

      swatch_list.forEach((swatch, index) => {
        swatch.addEventListener("mouseover", function () {
          [].forEach.call(swatch_list, function (el) {
            el.classList.remove("active");
          });
          if (image) {
            image.setAttribute("srcset", swatch.dataset.srcset);
          }

          swatch.classList.add("active");
        });
        swatch.addEventListener("click", function (evt) {
          window.location.href = this.dataset.href;
          evt.preventDefault();
        });
      });
    }
    enableQuickview(quickview_button) {
      const _this = this,
        drawer = document.getElementById("Product-Drawer"),
        body = document.body;

      quickview_button.addEventListener("click", function (evt) {
        evt.preventDefault();
        let productHandle = quickview_button.dataset.productHandle,
          productUrl = `${theme.routes.root_url}/products/${productHandle}`;

        // remove double `/` in case shop might have /en or language in URL
        productUrl = productUrl.replace("//", "/");
        const href = `${productUrl}?view=quick-view`;
        if (!productHandle) {
          return;
        }
        if (quickview_button.classList.contains("loading")) {
          return;
        }
        quickview_button.classList.add("loading");
        Promise.all([
          fetch(href, {
            method: "GET",
          }),
          loadProductResources(),
        ])
          .then(([response]) => {
            if (!response.ok) {
              throw new Error(`Failed to load ${href}: ${response.status}`);
            }
            quickview_button.classList.remove("loading");
            return response.text();
          })
          .then((text) => {
            const content = new DOMParser()
              .parseFromString(text, "text/html")
              .querySelector("#Product-Drawer-Content");
            if (!content) throw new Error(`${href} did not return quick-view content`);
            const sectionInnerHTML = content.innerHTML;
            _this.renderQuickview(sectionInnerHTML, drawer, body);
          })
          .catch((error) => {
            quickview_button.classList.remove("loading");
            console.error(error);
            window.location.assign(productUrl);
          });
      });
    }
    renderQuickview(sectionInnerHTML, drawer, body) {
      if (sectionInnerHTML) {
        drawer.querySelector("#Product-Drawer-Content").innerHTML =
          sectionInnerHTML;
        let quantity = drawer.querySelector(".quantity:not(.buttons_added)");
        if (quantity) {
          new QuantityInput(quantity);
        }

        setTimeout(() => {
          if (Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
        }, 300);
        body.classList.add("open-cc");
        drawer.classList.add("active");
        setTimeout(() => {
          drawer
            .querySelector(".product-quick-images--container")
            .classList.add("active");
        });
        drawer.querySelector(".side-panel-close").focus();
      }
    }
  }
  customElements.define("product-card", ProductCard);
}

if (document.querySelector("product-form, variant-selects, variant-radios")) {
  loadProductResources().catch((error) => console.error(error));
}
loadLiteYoutube().catch((error) => console.error(error));
observeLiteYoutube();

document.addEventListener("shopify:section:load", (event) => {
  if (event.target.querySelector("product-form, variant-selects, variant-radios")) {
    loadProductResources().catch((error) => console.error(error));
  }
  loadLiteYoutube(event.target).catch((error) => console.error(error));
});

/**
 *  @class
 *  @function Header
 */
class Header {
  constructor() {
    const header = document.querySelector(".header-section"),
      header_main = document.getElementById("header"),
      menu = document.getElementById("mobile-menu"),
      toggle = document.querySelector(".mobile-toggle-wrapper"),
      btnBack = document.querySelector(".header-back-btn"),
      setHeaderOffset = this.setHeaderOffset,
      setAnnouncementHeight = this.setAnnouncementHeight,
      setHeaderHeight = this.setHeaderHeight;

    if (!header_main) {
      return;
    }

    if (btnBack) {
      btnBack.addEventListener("click", (e) => {
        if (history.length > 1) {
          history.back();
        } else {
          window.location.href = "/";
        }
        e.preventDefault();
      });
    }

    document.addEventListener("keyup", (e) => {
      if (e.code) {
        if (e.code.toUpperCase() === "ESCAPE" && toggle) {
          toggle.removeAttribute("open");
          toggle.classList.remove("active");
        }
      }
    });
    const mobileToggle = toggle?.querySelector(".mobile-toggle");
    if (toggle && mobileToggle) mobileToggle.addEventListener("click", (e) => {
        if (header) setAnnouncementHeight(header);
      if (toggle.classList.contains("active")) {
        e.preventDefault();
        document.body.classList.remove("overflow-hidden");
        toggle.classList.remove("active");
        this.closeAnimation(toggle);
      } else {
        document.body.classList.add("overflow-hidden");
        setTimeout(() => {
          toggle.classList.add("active");
        });
      }
      window.dispatchEvent(new Event("resize.resize-select"));
    });

    // Mobile Menu offset
    window.addEventListener(
      "scroll",
      function () {
        if (header) setHeaderOffset(header);
        setHeaderHeight(header_main);
        // Sticky Header Class
        if (header_main.classList.contains("header-sticky--active")) {
          document.body.classList.add("header-sticky");

          let offset =
            parseInt(header_main.getBoundingClientRect().top, 10) +
            document.documentElement.scrollTop;

          header_main.classList.toggle(
            "is-sticky",
            window.scrollY >= offset && window.scrollY > 0
          );
        }
      },
      {
        passive: true,
      }
    );
    window.dispatchEvent(new Event("scroll"));

    if (document.getElementById("shopify-section-announcement-bar")) {
      const a_bar = document.getElementById("shopify-section-announcement-bar");
      window.addEventListener(
        "resize",
        function () {
          setAnnouncementHeight(a_bar);
        },
        {
          passive: true,
        }
      );
      window.dispatchEvent(new Event("resize"));
    }
    // Buttons.
    menu
      ?.querySelectorAll("summary")
      .forEach((summary) =>
        summary.addEventListener("click", this.onSummaryClick.bind(this))
      );
    menu
      ?.querySelectorAll("button")
      .forEach((button) =>
        button.addEventListener("click", this.onCloseButtonClick.bind(this))
      );
  }
  setAnnouncementHeight(a_bar) {
    let h = a_bar.clientHeight;
    document.documentElement.style.setProperty(
      "--announcement-height",
      h + "px"
    );
  }
  setHeaderOffset(header) {
    let h = header.getBoundingClientRect().top;
    document.documentElement.style.setProperty("--header-offset", h + "px");
  }
  setHeaderHeight(header) {
    let h = header.clientHeight;
    document.documentElement.style.setProperty("--header-height", h + "px");
  }
  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".link-container");
    const isOpen = detailsElement.hasAttribute("open");

    setTimeout(() => {
      detailsElement.classList.add("menu-opening");
      parentMenuElement && parentMenuElement.classList.add("submenu-open");
    }, 100);
  }
  onCloseButtonClick(event) {
    event.preventDefault();
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }
  closeSubmenu(detailsElement) {
    detailsElement.classList.remove("menu-opening");
    this.closeAnimation(detailsElement);
  }
  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        detailsElement.querySelectorAll("details").forEach((details) => {
          details.removeAttribute("open");
          details.classList.remove("menu-opening");
          details.classList.remove("submenu-open");
        });
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}
/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get("full-menu")) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();

      this.submenus = this.querySelectorAll(
        ".thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu"
      );
    }
    connectedCallback() {
      if (!this.submenus.length) {
        return;
      }
      const _this = this;
      // resize on initial load
      document.fonts.ready.then(function () {
        window.addEventListener(
          "resize",
          debounce(function () {
            _this.resizeSubMenus();
          }, 100)
        );
      });
    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(
          ":scope >.menu-item-has-children>.sub-menu"
        );

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l =
              sub_submenu.parentElement.getBoundingClientRect().left +
              sub_submenu.parentElement.offsetWidth,
            total = w + l;

          if (total > window.innerWidth) {
            sub_submenu.parentElement.classList.add("left-submenu");
          } else if (
            sub_submenu.parentElement.classList.contains("left-submenu")
          ) {
            sub_submenu.parentElement.classList.remove("left-submenu");
          }
        });
      });
    }
  }
  customElements.define("full-menu", FullMenu);
}
/**
 *  @class
 *  @function PanelClose
 */
class PanelClose extends HTMLElement {
  constructor() {
    super();
    let cc = document.querySelector(".click-capture");

    // Add functionality to buttons
    this.addEventListener("click", (e) => this.close_panel(e));
    document.addEventListener("keydown", (e) => {
      if (e.code.toUpperCase() === "ESCAPE") {
        document.querySelectorAll(".side-panel").forEach((panel) => {
          if (panel.classList.contains("product-drawer")) {
            if (panel.querySelector(".product-quick-images--container")) {
              panel
                .querySelector(".product-quick-images--container")
                .classList.remove("active");
            }
            if (window.innerWidth < 1069) {
              panel.classList.remove("active");
              document.body.classList.remove("open-cc");
            } else {
              if (panel.querySelector(".product-quick-images--container")) {
                panel
                  .querySelector(".product-quick-images--container")
                  .addEventListener("transitionend", function () {
                    panel.classList.remove("active");
                    document.body.classList.remove("open-cc");
                  });
              }
            }
          } else {
            panel.classList.remove("active");
            document.body.classList.remove("open-cc");
          }
        });
      }
    });
    cc.addEventListener("click", (e) => {
      let panel = document.querySelector(".side-panel.active");
      if (panel) {
        if (panel.classList.contains("product-drawer")) {
          panel
            .querySelector(".product-quick-images--container")
            .classList.remove("active");
          if (window.innerWidth < 1069) {
            panel.classList.remove("active");
            document.body.classList.remove("open-cc");
          } else {
            panel
              .querySelector(".product-quick-images--container")
              .addEventListener("transitionend", function () {
                panel.classList.remove("active");
                document.body.classList.remove("open-cc");
              });
          }
        } else {
          panel.classList.remove("active");
          document.body.classList.remove("open-cc");
        }
      }
    });
  }

  close_panel(e) {
    e.preventDefault();
    let panel = e.target.closest(".side-panel");
    if (panel.classList.contains("product-drawer")) {
      if (panel.querySelector(".product-quick-images--container")) {
        panel
          .querySelector(".product-quick-images--container")
          .classList.remove("active");
      }
      if (window.innerWidth < 1069) {
        panel.classList.remove("active");
        document.body.classList.remove("open-cc");
      } else {
        if (panel.querySelector(".product-quick-images--container")) {
          panel
            .querySelector(".product-quick-images--container")
            .addEventListener("transitionend", function () {
              panel.classList.remove("active");
              document.body.classList.remove("open-cc");
            });
        }
      }
    } else {
      panel.classList.remove("active");
      document.body.classList.remove("open-cc");
    }
  }
}
customElements.define("side-panel-close", PanelClose);

/**
 *  @class
 *  @function CartDrawer
 */
class CartDrawer {
  constructor() {
    this.container = document.getElementById("Cart-Drawer");

    if (!this.container) {
      return;
    }
    let _this = this,
      button = document.getElementById("cart-drawer-toggle"),
      quantities = this.container.querySelectorAll(".quantity input"),
      tabs = this.container.querySelectorAll(".side-panel-tab"),
      vendorCheckoutBtns = this.container.querySelectorAll(
        ".vendor_checkout_btn"
      ),
      lastVendor = localStorage.getItem("yd-last-vendor");

    // Add functionality to buttons
    if (button) {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        if (location.pathname != "/cart") {
          document.body.classList.add("open-cc");
          this.container.classList.add("active");
          this.container.focus();
        }
      });
    }

    // Add functionality to tabs
    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        this.setTab(tab.dataset.vendor);
      });
    });
    this.setTab(lastVendor);

    // Add functionality to checkout btns
    if (vendorCheckoutBtns.length > 1) {
      vendorCheckoutBtns.forEach((btn) => {
        btn.addEventListener("click", (event) => {
          this.vendorCheckout(btn);
          event.preventDefault();
        });
      });
    }

    // tabs scroll
    this.tabsScroll();

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.container.addEventListener(
      "change",
      this.debouncedOnChange.bind(this)
    );

    this.notesToggle();
    this.removeProductEvent();
  }
  onChange(event) {
    if (!this.container.contains(event.target)) return;
    if (event.target.classList.contains("qty")) {
      this.updateQuantity(event.target.dataset.index, event.target.value);
    }
  }
  removeProductEvent() {
    if (this.removeProductHandler) return;
    this.removeProductHandler = (event) => {
      const remove = event.target.closest(".remove");
      if (!remove || !this.container.contains(remove)) return;
      event.preventDefault();
      this.updateQuantity(remove.dataset.index, 0);
    };
    this.container.addEventListener("click", this.removeProductHandler);
  }
  getSectionsToRender() {
    return [
      {
        id: "Cart-Drawer",
        section: "cart-drawer",
        selector: ".cart-drawer",
      },
      {
        id: "cart-drawer-toggle",
        section: "cart-bubble",
        selector: ".thb-item-count",
      },
    ];
  }
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
  notesToggle() {
    let notes_toggle = document.querySelectorAll(".order-note-toggle");

    if (!notes_toggle) {
      return;
    }

    notes_toggle.forEach((toggle) => {
      toggle.addEventListener("click", (event) => {
        toggle.nextElementSibling.classList.add("active");
      });
      toggle.nextElementSibling
        .querySelectorAll(".button, .order-note-toggle__content-overlay")
        .forEach((el) => {
          el.addEventListener("click", (event) => {
            toggle.nextElementSibling.classList.remove("active");
          });
        });
    });
  }

  updateQuantity(line, quantity) {
    const container = this.container.querySelector(`#CartDrawerItem-${line}`);
    const itemKey = container?.querySelector(".quantity")?.dataset.id;
    const newQuantity = Number(quantity);
    if (
      this.isUpdating ||
      !itemKey ||
      quantity === "" ||
      !Number.isInteger(newQuantity) ||
      newQuantity < 0
    ) return;
    this.isUpdating = true;
    container.classList.add("thb-loading");

    const body = JSON.stringify({
      id: itemKey,
      quantity: newQuantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    return fetch(`${theme.routes.cart_change_url}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
      },
      ...{
        body,
      },
    })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        if (parsedState.errors) return;

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);

          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );

          let quantities = elementToReplace.querySelectorAll(
            ".quantity:not(.buttons_added)"
          );
          let tabs = elementToReplace.querySelectorAll(".side-panel-tab");
          if (quantities) {
            quantities.forEach((quantity) => {
              new QuantityInput(quantity);
            });
          }

          tabs.forEach((tab) => {
            tab.addEventListener("click", () => {
              this.setTab(tab.dataset.vendor);
            });
          });

          this.notesToggle();
        });

        this.setTab(localStorage.getItem("yd-last-vendor"));
        this.tabsScroll();
        document.dispatchEvent(new CustomEvent("line-item:change:end", { detail: { quantity: newQuantity, cart: parsedState } }));
      })
      .catch((error) => console.error(error))
      .finally(() => {
        this.isUpdating = false;
        container.classList.remove("thb-loading");
      });
  }

  tabsScroll() {
    const slider = document.querySelector(".side-panel-tabs");
    if (!slider) {
      return;
    }
    let isDown = false;
    let startX;
    let scrollLeft;

    slider.addEventListener("mousedown", (e) => {
      isDown = true;
      slider.classList.add("active");
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });
    slider.addEventListener("mouseleave", () => {
      isDown = false;
      slider.classList.remove("active");
    });
    slider.addEventListener("mouseup", () => {
      isDown = false;
      slider.classList.remove("active");
    });
    slider.addEventListener("mousemove", (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 3; //scroll-fast
      slider.scrollLeft = scrollLeft - walk;
      //console.log(walk);
    });
  }

  setTab(vendor) {
    const tabContent = this.container.querySelectorAll(".side-panel-content");
    const tabFooter = this.container.querySelectorAll(".side-panel-footer");
    const tabs = this.container.querySelectorAll(".side-panel-tab");

    function removeClass(elements) {
      elements.forEach((el) => {
        el.classList.remove("active");
      });
    }

    tabs.forEach((tab) => {
      if (tab.dataset.vendor == vendor) {
        removeClass(tabs);
        tab.classList.add("active");
      }
    });

    tabContent.forEach((content) => {
      if (content.dataset.vendor == vendor) {
        removeClass(tabContent);
        content.classList.add("active");
      }
    });

    tabFooter.forEach((footer) => {
      if (footer.dataset.vendor == vendor) {
        removeClass(tabFooter);
        footer.classList.add("active");
      }
    });

    localStorage.setItem("yd-last-vendor", vendor);
  }

  async vendorCheckout(btn) {
    const getCart = await fetch("/cart.js");
    const currentCart = await getCart.json();
    const vendor = btn.dataset.vendor;
    const note = document.querySelector(
      `.vendor-note[data-vendor=${CSS.escape(vendor)}]`
    ).value;

    this.container.classList.add("cart-disabled");
    btn.classList.add("loading");

    const saveItems = function () {
      let items = [];
      let lines = {};
      for (let i = 0; i < currentCart.items.length; i++) {
        if (vendor != currentCart.items[i].vendor) {
          let item = {
            id: currentCart.items[i].variant_id,
            quantity: currentCart.items[i].quantity,
            selling_plan: currentCart.items[i].selling_plan_allocation
              ? currentCart.items[i].selling_plan_allocation.selling_plan.id
              : null,
          };
          items.push(item);
          lines[currentCart.items[i].key] = 0;
        }
      }
      return [items, lines];
    };

    const savedItems = saveItems();
    //update note and delete items
    const updateCart = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        note: note,
        updates: savedItems[1],
      }),
    });
    window.localStorage.setItem("yd-cart-items", JSON.stringify(savedItems[0]));
    window.localStorage.setItem("yd-cart-need-update", "true");
    window.location.href = "/checkout";
  }
}

/**
 *  @class
 *  @function ModalDialog
 */
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target.nodeName === "MODAL-DIALOG") this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    this.querySelectorAll("iframe[data-modal-src]").forEach((iframe) => {
      if (iframe.getAttribute("src") !== iframe.dataset.modalSrc) {
        iframe.setAttribute("src", iframe.dataset.modalSrc);
      }
    });
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    this.removeAttribute("open");
    this.querySelectorAll("iframe[data-modal-src]").forEach((iframe) =>
      iframe.setAttribute("src", "about:blank")
    );
    this.querySelectorAll(".js-youtube").forEach((video) => {
      video.contentWindow.postMessage(
        '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
        "*"
      );
    });
    this.querySelectorAll(".js-vimeo").forEach((video) => {
      video.contentWindow.postMessage('{"method":"pause"}', "*");
    });
    this.querySelectorAll("video").forEach((video) => video.pause());

    this.querySelectorAll("iframe:not([data-modal-src])").forEach((video) => {
      video.src = video.src;
    });
  }
}
customElements.define("modal-dialog", ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", (e) => {
      e.preventDefault();
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

/**
 *  @class
 *  @function Localization
 */
class Localization {
  constructor() {
    let _this = this;
    // resize on initial load
    document
      .querySelectorAll(".thb-localization-forms")
      .forEach((localization) => {
        localization.addEventListener("change", (e) => {
          localization.querySelector("form").submit();
        });
      });
  }
}

/**
 *  @class
 *  @function SelectWidth
 */
class SelectWidth {
  constructor() {
    let _this = this;
    // resize on initial load
    document.fonts.ready.then(function () {
      document.querySelectorAll(".resize-select").forEach(_this.resizeSelect);
    });

    // delegated listener on change
    document.body.addEventListener("change", (e) => {
      if (
        e.target.matches(".resize-select") &&
        e.target.offsetParent !== null
      ) {
        _this.resizeSelect(e.target);
      }
    });
    window.addEventListener("resize.resize-select", function () {
      document.querySelectorAll(".resize-select").forEach(_this.resizeSelect);
    });
  }

  resizeSelect(sel) {
    let tempOption = document.createElement("option");
    tempOption.textContent = sel.selectedOptions[0].textContent;

    let tempSelect = document.createElement("select");
    tempSelect.style.visibility = "hidden";
    tempSelect.style.position = "fixed";
    tempSelect.appendChild(tempOption);

    sel.after(tempSelect);
    if (tempSelect.clientWidth > 0) {
      sel.style.width = `${+tempSelect.clientWidth + 12}px`;
    }
    tempSelect.remove();
  }
}

if (typeof SelectWidth !== "undefined") {
  new SelectWidth();
}

/**
 *  @class
 *  @function FooterMenuToggle
 */
class FooterMenuToggle {
  constructor() {
    let _this = this;
    // resize on initial load
    document
      .querySelectorAll(".thb-widget-title.collapsible")
      .forEach((button) => {
        button.addEventListener("click", (e) => {
          button.classList.toggle("active");
        });
      });
  }
}

/**
 *  @class
 *  @function SidePanelContentTabs
 */
if (!customElements.get("side-panel-content-tabs")) {
  class SidePanelContentTabs extends HTMLElement {
    constructor() {
      super();
      this.buttons = this.querySelectorAll("button");
      this.panels = this.parentElement.querySelectorAll(
        ".side-panel-content--tab-panel"
      );
    }
    connectedCallback() {
      this.setupButtonObservers();
    }
    disconnectedCallback() {}
    setupButtonObservers() {
      this.buttons.forEach((item, i) => {
        item.addEventListener("click", (e) => {
          this.toggleActiveClass(i);
        });
      });
    }
    toggleActiveClass(i) {
      this.buttons.forEach((button) => {
        button.classList.remove("tab-active");
      });
      this.buttons[i].classList.add("tab-active");

      this.panels.forEach((panel) => {
        panel.classList.remove("tab-active");
      });
      this.panels[i].classList.add("tab-active");
    }
  }

  customElements.define("side-panel-content-tabs", SidePanelContentTabs);
}

window.addEventListener("load", () => {
  if (typeof Localization !== "undefined") {
    new Localization();
  }
  if (typeof CartDrawer !== "undefined") {
    new CartDrawer();
  }
  if (typeof Header !== "undefined") {
    new Header();
  }
  if (typeof FooterMenuToggle !== "undefined") {
    new FooterMenuToggle();
  }
});

var links = document.links;
for (let i = 0, linksLength = links.length; i < linksLength; i++) {
  if (links[i].hostname !== window.location.hostname) {
    links[i].target = "_blank";
    links[i].relList.add("noopener", "noreferrer");
  }
}

//table of contents
const tableContentContainer = document.querySelector(".tablecontents:not(.article-toc)");
const tableContentButton = document.querySelector(".tablecontentscaption");
if (tableContentContainer && tableContentButton) {
  tableContentButton.onclick = function () {
    tableContentContainer.classList.toggle("collapsed");
  };
}

//popup youtube video
(function () {
  function createPopup() {
    const popup = document.createElement("div");
    popup.id = "media-popup";
    const popupIframe = document.createElement("iframe");
    popupIframe.setAttribute("src", "");
    popupIframe.setAttribute("allowfullscreen", "");
    popupIframe.setAttribute("frameborder", "0");
    document.body.prepend(popup);
    popup.appendChild(popupIframe);
  }

  createPopup();

  const buttons = document.querySelectorAll("[popup-youtube]");
  const modal = document.querySelector("#media-popup");
  const iframe = document.querySelector("#media-popup iframe");

  modal.onclick = function () {
    document.body.classList.toggle("show-popup");
    iframe.src = "";
  };

  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      const videoId = button.getAttribute("popup-youtube");
      document.body.classList.toggle("show-popup");
      iframe.src = "https://www.youtube.com/embed/" + videoId;
    });
  });

  //legacy code
  const buttons_legacy = document.querySelectorAll("[data-media]");
  buttons_legacy.forEach((button) => {
    button.addEventListener("click", () => {
      const videoId = button.getAttribute("data-media");
      document.body.classList.toggle("show-popup");
      iframe.src = videoId;
    });
  });
  //end legacy code
})();

//restore cart items on load page
async function restoreCart() {
  let items;
  try {
    items = JSON.parse(localStorage.getItem("yd-cart-items") || "null");
  } catch (_) {
    window.localStorage.removeItem("yd-cart-items");
    window.localStorage.setItem("yd-cart-need-update", "false");
    return undefined;
  }

  if (!Array.isArray(items) || items.length === 0 || localStorage.getItem("yd-cart-need-update") != "true") {
    return undefined;
  }

  window.localStorage.setItem("yd-cart-need-update", "false");
  const parser = new DOMParser();
  const body = JSON.stringify({
    items,
    sections: "cart-drawer,cart-bubble",
  });

  const updateCart = await fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    ...{
      body,
    },
  });
  const state = await updateCart.json();
  const parsedCartBubble = parser.parseFromString(
    state.sections["cart-bubble"],
    "text/html"
  );
  const count = parsedCartBubble.querySelector(".thb-item-count").innerHTML;
  document.querySelector(".thb-item-count").innerHTML = count;
  const parsedCartDrawer = parser.parseFromString(
    state.sections["cart-drawer"],
    "text/html"
  );
  const drawer = parsedCartDrawer.querySelector(".cart-drawer").innerHTML;
  document.querySelector(".cart-drawer").innerHTML = drawer;

  //clear cart note
  fetch("/cart/update.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      note: " ",
    }),
  });

  if (typeof CartDrawer !== "undefined") {
    new CartDrawer();
  }
  let quantities = document.querySelectorAll(
    ".cart-drawer .quantity:not(.buttons_added)"
  );
  quantities.forEach((quantity) => {
    new QuantityInput(quantity);
  });
}

window.addEventListener("load", () => {
  if (location.pathname != "/cart") {
    restoreCart();
  }
});

document.querySelectorAll(".faq-metaobject-question img:not([alt])").forEach((image) => {
  let filename = "";

  try {
    filename = decodeURIComponent(new URL(image.src, window.location.href).pathname)
      .split("/")
      .pop()
      .replace(/\.[^.]+$/, "")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[-_]+/g, " ")
      .trim();
  } catch (error) {
    filename = "";
  }

  const questionTitle = image
    .closest(".faq-metaobject-question")
    ?.querySelector(".faq-metaobject-question__title")
    ?.textContent.trim();

  image.alt = image.title || filename || questionTitle || "";
});


/**
 *  @class
 *  @function CollapsibleRow
 */
if (!customElements.get("collapsible-row")) {
  class CollapsibleRow extends HTMLElement {
    constructor() {
      super();

      this.details = this.querySelector("details");
      this.summary = this.querySelector("summary");
      this.content = this.querySelector(".collapsible__content");
    }
    connectedCallback() {
      this.setListeners();
    }
    setListeners() {
      if (
        document.body.classList.contains("animations-true") &&
        typeof gsap !== "undefined"
      ) {
        setTimeout(() => {
          this.prepareAnimations();
          this.querySelector("summary").addEventListener("click", (e) =>
            this.onClick(e)
          );
        }, 400);
      }
    }
    prepareAnimations() {
      let _this = this,
        summary_height = this.querySelector("summary").offsetHeight,
        content_height = this.querySelector(
          ".collapsible__content"
        ).offsetHeight,
        initial_height = summary_height,
        final_height = summary_height + content_height;

      this.tl = false;
      this.tl = gsap.timeline({
        reversed: !_this.details.open,
        paused: true,
        inherit: false,
        ease: "sine.inOut",
        onStart: function () {
          _this.details.open = true;
          _this.details.style.overflow = "hidden";
        },
        onReverseComplete: function () {
          _this.details.open = false;
          _this.details.style.overflow = "";
        },
      });

      this.tl.fromTo(
        _this.details,
        {
          height: function () {
            let h = Math.max(Math.max(initial_height, 0), 24);
            return h;
          },
          clearProps: "height",
        },
        {
          duration: 0.6,
          height: final_height,
          clearProps: "height",
        }
      );

      if (this.details.open) {
        this.tl.progress(1);
      }
    }
    instantClose() {
      this.tl.timeScale(10).reverse();
    }
    animateClose() {
      this.tl.timeScale(3).reverse();
    }
    animateOpen() {
      this.tl.timeScale(1).play();
    }
    onClick(e) {
      e.preventDefault();
      if (this.tl.reversed()) {
        this.tl.timeScale(1).play();
      } else {
        this.tl.timeScale(3).reverse();
      }
      setTimeout(
        () =>
          this.details.scrollIntoView({ behavior: "smooth", block: "start" }),
        300
      );
    }
  }
  customElements.define("collapsible-row", CollapsibleRow);
}
